import { useMutation } from "@apollo/client";
import { Alert, FormControl, Stack, Typography } from "@mui/material";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import { Dialog } from "components/Dialog";
import { RolePicker } from "components/RolePicker";
import { getFullName } from "encamp-shared/src/utils/name";
import { gql } from "generated-graphql";
import { Role } from "generated-graphql/graphql";
import { useMemo, useState } from "react";

import { useAlerts } from "../../../components/Alerts/AlertProvider";
import { prettyPrintUserStatus, UserRow } from "./index";

const BULK_REASSIGN_USER_ROLE = gql(`
  mutation BulkReassignUserRole($userIds: [ID!]!, $tenantId: ID!, $roleId: ID!) {
    bulkReassignUserRole(userIds: $userIds, tenantId: $tenantId, roleId: $roleId)
  }
`);

type BulkReassignRoleDialogProps = {
  selectedUsers: UserRow[];
  tenantId: string;
  onClose: () => void;
  onSaved: () => void;
};

type ReassignTableRow = {
  id: string;
  name: string;
  email: string;
  currentRole: string;
  status: string;
};

/**
 * Bulk-reassign role for a set of users in the current tenant.
 *
 * Matches the BulkActions dialog pattern used elsewhere in the app
 * (`apps/oak/src/components/BulkActions/`): a header summary with a live
 * count, an inline DataGrid showing each affected row, and an Alert for
 * users that will be skipped (already on the target role).
 */
export function BulkReassignRoleDialog({
  selectedUsers,
  tenantId,
  onClose,
  onSaved,
}: BulkReassignRoleDialogProps) {
  const alerts = useAlerts();
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);

  const [bulkReassign, { loading }] = useMutation(BULK_REASSIGN_USER_ROLE, {
    refetchQueries: ["Users"],
    awaitRefetchQueries: true,
  });

  const userCount = selectedUsers.length;
  const userNoun = userCount === 1 ? "user" : "users";

  const tableRows: ReassignTableRow[] = useMemo(
    () =>
      selectedUsers.map((u) => {
        const ut = u.UserTenant.find((t) => t.tenantId === tenantId);
        return {
          id: u.id,
          name: getFullName(u.person) || "—",
          email: u.email,
          currentRole: ut?.role?.name ?? "—",
          status: prettyPrintUserStatus(u.status),
        };
      }),
    [selectedUsers, tenantId]
  );

  const usersAlreadyOnRole = useMemo(
    () => (selectedRole ? tableRows.filter((r) => r.currentRole === selectedRole.name) : []),
    [selectedRole, tableRows]
  );
  const usersToActuallyChange = userCount - usersAlreadyOnRole.length;

  const columns: CustomGridColDef<ReassignTableRow>[] = useMemo(
    () => [
      { field: "name", headerName: "Name", flex: 1, minWidth: 160 },
      { field: "email", headerName: "Email", flex: 1.4, minWidth: 220 },
      { field: "currentRole", headerName: "Current role", flex: 1, minWidth: 160 },
      { field: "status", headerName: "Status", width: 130 },
    ],
    []
  );

  const handleConfirm = async () => {
    if (!selectedRole) return;
    try {
      const result = await bulkReassign({
        variables: {
          userIds: selectedUsers.map((u) => u.id),
          tenantId,
          roleId: selectedRole.id,
        },
      });
      const updated = result.data?.bulkReassignUserRole ?? 0;
      alerts.success(
        updated === 1
          ? `Reassigned 1 user to ${selectedRole.name}`
          : `Reassigned ${updated} users to ${selectedRole.name}`
      );
      onSaved();
    } catch (error) {
      alerts.error(error instanceof Error ? error.message : "Failed to reassign users");
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="md"
      title="Reassign role"
      description={
        selectedRole
          ? `${usersToActuallyChange} of ${userCount} ${userNoun} will be reassigned to "${selectedRole.name}".`
          : `Choose a role to assign to the ${userCount} selected ${userNoun} below.`
      }
      primaryActionButtonProps={{
        loading,
        onClick: handleConfirm,
        disabled: !selectedRole || usersToActuallyChange === 0,
        saveText: selectedRole
          ? usersToActuallyChange === 1
            ? "Reassign 1 user"
            : `Reassign ${usersToActuallyChange} users`
          : "Reassign",
      }}
    >
      <Stack spacing={3} sx={{ mt: 1 }}>
        <FormControl fullWidth>
          <RolePicker
            tenantId={tenantId}
            value={selectedRole?.id ?? ""}
            onChange={setSelectedRole}
          />
        </FormControl>

        <Stack spacing={1}>
          <Typography variant="subtitle2">Users ({userCount} selected)</Typography>
          <DataGrid<ReassignTableRow>
            rows={tableRows}
            columns={columns}
            disableRowSelectionOnClick
            pagination
            paginationMode="client"
            pageSizeOptions={[5, 10, 25]}
            initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
            sx={{ maxHeight: 340 }}
          />
        </Stack>

        {selectedRole && usersAlreadyOnRole.length > 0 && (
          <Alert severity="info" variant="outlined">
            {usersAlreadyOnRole.length} of {userCount} {userNoun} already have the role{" "}
            <strong>{selectedRole.name}</strong> — they&apos;ll be skipped.
          </Alert>
        )}
      </Stack>
    </Dialog>
  );
}
